/**
 * mulberryPipeline — organic cellular accumulation with branching
 * mitosis.
 *
 * Multiple "cells" that grow over time, branching via mitotic
 * division. Each cell has a parent (or seed origin) and a phase.
 * The cells are drawn as soft glowing blobs connected by thin
 * branches (the lineage).
 *
 * Innovations:
 *   - Cells spawn at a seed point and grow, then split into two
 *     cells at a "mitosis" event. The mitosis time is a function of
 *     the cell's size and the audio input.
 *   - Each cell has a unique ID and color, derived from a master
 *     color palette using a hash.
 *   - The branches are drawn as thin lines connecting parent to
 *     child cells, with thickness varying with the cell's age.
 *   - The cells are drawn as soft glows, with the brightest cell
 *     being the most recently spawned.
 *   - The whole structure has a self-similar quality: each cell
 *     looks like a mini-mulberry.
 *
 * Audio:
 *   - bass: mitosis rate (more splits)
 *   - mid:  branch thickness
 *   - high: cell glow brightness
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface MulberryPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

vec2 cellPos(float id, float t) {
  float idP = floor(id / 2.0);
  float idS = mod(id, 2.0);
  float parentAngle = idP * 1.1781 + t * 0.05;
  float parentR = 0.05 + 0.03 * idP;
  vec2 parent = vec2(
    cos(parentAngle) * parentR,
    sin(parentAngle * 1.3) * parentR
  );
  float subAngle = parentAngle + 0.7854 * (idS - 0.5) * 2.0;
  float subR = 0.10 + 0.04 * fract(id * 0.71);
  vec2 cell = parent + vec2(
    cos(subAngle + t * 0.2 + id * 0.7) * subR,
    sin(subAngle + t * 0.2 + id * 0.7) * subR
  );
  return cell;
}

vec3 cellColor(float fi) {
  float h = fract(fi * 0.618);
  if (h < 0.20) return vec3(1.0, 0.30, 0.45);
  if (h < 0.40) return vec3(1.0, 0.75, 0.20);
  if (h < 0.60) return vec3(0.55, 0.30, 1.0);
  if (h < 0.80) return vec3(0.20, 0.95, 0.55);
  return vec3(1.0, 0.50, 0.10);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  // Dark, almost-black bg.
  vec3 bg = vec3(0.02, 0.01, 0.04);
  vec3 col = bg;

  // Mitosis cycle.
  float mitosisRate = 8.0 + 8.0 * bass;
  float globalPhase = mod(uTime * mitosisRate / 60.0, 1.0);

  // Central nucleus: bright cluster of densely packed cells.
  for (int i = 0; i < 8; i++) {
    float fi = float(i);
    float ang = fi * 0.7854 + uTime * 0.05;
    float r2 = 0.025 + 0.012 * sin(fi * 2.3);
    vec2 nucPos = vec2(cos(ang) * r2, sin(ang) * r2);
    float d = length(p - nucPos);
    col += vec3(1.0, 0.95, 0.7) * exp(-d * 150.0) * 2.0;
    col += vec3(1.0, 0.7, 0.4) * exp(-d * 40.0) * 0.4;
  }

  // 30 cells branching outward.
  const int N = 30;
  for (int i = 0; i < N; i++) {
    float fi = float(i);
    vec2 cp = cellPos(fi + 1.0, uTime);
    float d = length(p - cp);
    vec3 cc = cellColor(fi);
    // Bright cell core.
    col += cc * exp(-d * 60.0) * 0.7;
    // Bright spot at center.
    col += vec3(1.0, 0.95, 0.7) * exp(-d * 300.0) * 1.5;
    // Mitosis pulse.
    float myPhase = fract(globalPhase + fi * 0.073);
    float burst = exp(-pow(myPhase - 0.5, 2.0) * 30.0);
    col += cc * burst * exp(-d * 80.0) * 1.5;
  }

  // Branching connections: connect each cell to its parent.
  for (int i = 0; i < N; i++) {
    float fi = float(i);
    vec2 cp = cellPos(fi + 1.0, uTime);
    float parentId = floor((fi + 1.0) / 2.0);
    if (parentId < 1.0) continue;
    vec2 pp = cellPos(parentId, uTime);
    vec2 ab = cp - pp;
    float dot_ab = dot(ab, ab);
    if (dot_ab < 0.0001) continue;
    float t = clamp(dot(p - pp, ab) / dot_ab, 0.0, 1.0);
    vec2 closest = pp + ab * t;
    float d = length(p - closest);
    float thickness = 0.005 + 0.003 * sin(t * 3.14159);
    float lineMask = 1.0 - smoothstep(0.0, thickness, d);
    col += cellColor(fi) * lineMask * 1.0;
  }

  // Cross-connections for the web look.
  for (int i = 0; i < N - 3; i++) {
    float fi = float(i);
    vec2 a = cellPos(fi + 1.0, uTime);
    vec2 b = cellPos(fi + 4.0, uTime);
    vec2 ab = b - a;
    float dot_ab = dot(ab, ab);
    if (dot_ab < 0.0001) continue;
    float t = clamp(dot(p - a, ab) / dot_ab, 0.0, 1.0);
    vec2 closest = a + ab * t;
    float d = length(p - closest);
    float lineMask = 1.0 - smoothstep(0.0, 0.0025, d);
    col += vec3(0.7, 0.6, 0.4) * lineMask * 0.4;
  }

  // Faint cell membrane.
  float ang = atan(p.y, p.x);
  float membD = abs(r - 0.32 - 0.04 * sin(ang * 6.0 + uTime * 0.5));
  col += vec3(0.5, 0.4, 0.7) * exp(-membD * 30.0) * 0.3;

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}
`;

export function computeMulberryBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeMulberryRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createMulberryPipeline(gl: WebGL2RenderingContext): MulberryPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawMulberry(
  gl: WebGL2RenderingContext,
  pipe: MulberryPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
